import React, { useEffect, useRef } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useGetOne, useRecordContext } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import OpportunityProfileForm from './OpportunityProfileForm.tsx';

type OpportunityProfileInlineProps = {
  onLoaded?: (data: Record<string, unknown>) => void;
  externalProfile?: Record<string, unknown> | null;
};

const OpportunityProfileInline: React.FC<OpportunityProfileInlineProps> = ({
  onLoaded,
  externalProfile
}) => {
  const record = useRecordContext<{ id?: number }>();
  const { setValue } = useFormContext();
  const { data, isLoading } = useGetOne(
    'opportunity-profiles',
    { id: record?.id ?? 0 },
    { enabled: Boolean(record?.id) }
  );
  const lastLoadedId = useRef<number | null>(null);

  const applyProfileToForm = (profile: Record<string, unknown>) => {
    const {
      owners,
      entities,
      locations,
      opportunityId,
      id,
      createdAt,
      updatedAt,
      tenantId,
      ...rest
    } = profile;

    Object.entries(rest).forEach(([key, value]) => {
      setValue(key, value, { shouldDirty: false });
    });

    if (owners) setValue('owners', owners, { shouldDirty: false });
    if (entities) setValue('entities', entities, { shouldDirty: false });
    if (locations) setValue('locations', locations, { shouldDirty: false });
    setValue('profileLoaded', true, { shouldDirty: false });
  };

  useEffect(() => {
    if (!data) return;
    if (record?.id && lastLoadedId.current === record.id) return;
    if (record?.id) lastLoadedId.current = record.id;
    applyProfileToForm(data as Record<string, unknown>);
    onLoaded?.(data as Record<string, unknown>);
  }, [data, record?.id, setValue]);

  useEffect(() => {
    if (!externalProfile) return;
    applyProfileToForm(externalProfile);
  }, [externalProfile, setValue]);

  if (!record?.id) return null;

  return (
    <Box>
      {isLoading ? <CircularProgress size={24} /> : null}
      <OpportunityProfileForm />
    </Box>
  );
};

export default OpportunityProfileInline;
